import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { Add, Edit, Delete, PhotoCamera, PhotoLibrary } from '@mui/icons-material';
import SetForm from './SetForm';
import SetPhotoManager from './SetPhotoManager';
import {
  dialogFormPaperSx,
  dialogFormScrollContentSx,
  dialogFormTitleSx,
  dialogFormActionsSx
} from '../utils/dialogFormLayout';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

const SetList = ({ characters = [] }) => {
  const [sets, setSets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [setToDelete, setSetToDelete] = useState(null);
  const [photoManagerOpen, setPhotoManagerOpen] = useState(false);
  const [selectedSetForPhotos, setSelectedSetForPhotos] = useState(null);

  useEffect(() => {
    fetchSets();
  }, []);

  const fetchSets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sets`);
      if (!response.ok) {
        throw new Error('Failed to fetch sets');
      }
      const data = await response.json();
      setSets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSet = async (setData) => {
    try {
      // Prepare data for creation, excluding coverFile and existingCoverImage
      const createData = {
        name: setData.name,
        price: setData.price,
        description: setData.description,
        instruction: setData.instruction,
        characterId: setData.characterId,
        tags: setData.tags,
        requiredRelationshipPoints: setData.requiredRelationshipPoints,
        rewardTitle: setData.rewardTitle,
        rewardDescription: setData.rewardDescription
      };

      const response = await fetch(`${API_BASE_URL}/api/sets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(createData),
      });

      if (!response.ok) {
        throw new Error('Failed to create set');
      }

      const newSet = await response.json();
      
      // If there's a cover file, upload it
      if (setData.coverFile) {
        console.log('Uploading cover file:', setData.coverFile);
        const formData = new FormData();
        formData.append('cover', setData.coverFile);
        
        console.log('FormData created, sending request...');
        const coverResponse = await fetch(`${API_BASE_URL}/api/sets/${newSet._id}/cover`, {
          method: 'POST',
          body: formData,
        });

        console.log('Cover upload response status:', coverResponse.status);
        
        if (coverResponse.ok) {
          const updatedSet = await coverResponse.json();
          console.log('Cover upload successful:', updatedSet);
          await fetchSets();
          return;
        } else {
          const errorText = await coverResponse.text();
          console.error('Cover upload failed:', errorText);
          throw new Error(`Cover upload failed: ${errorText}`);
        }
      }

      await fetchSets();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateSet = async (setData) => {
    try {
      console.log('Updating set with data:', setData);
      
      // Prepare data for update, excluding coverFile and existingCoverImage
      const updateData = {
        name: setData.name,
        price: setData.price,
        description: setData.description,
        instruction: setData.instruction,
        characterId: setData.characterId,
        tags: setData.tags,
        requiredRelationshipPoints: setData.requiredRelationshipPoints,
        rewardTitle: setData.rewardTitle,
        rewardDescription: setData.rewardDescription
      };

      // If there's an existing cover image and no new file, keep the existing one
      if (setData.existingCoverImage && !setData.coverFile) {
        updateData.coverImage = setData.existingCoverImage;
      }

      const response = await fetch(`${API_BASE_URL}/api/sets/${editingSet._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        throw new Error('Failed to update set');
      }

      const updatedSet = await response.json();
      console.log('Set updated successfully:', updatedSet);

      // If there's a cover file, upload it
      if (setData.coverFile) {
        console.log('Uploading cover file for update:', setData.coverFile);
        const formData = new FormData();
        formData.append('cover', setData.coverFile);
        
        const coverResponse = await fetch(`${API_BASE_URL}/api/sets/${editingSet._id}/cover`, {
          method: 'POST',
          body: formData,
        });

        console.log('Cover upload response status:', coverResponse.status);
        
        if (coverResponse.ok) {
          const finalSet = await coverResponse.json();
          console.log('Cover upload successful for update:', finalSet);
          await fetchSets();
          setEditingSet(null);
          return;
        } else {
          const errorText = await coverResponse.text();
          console.error('Cover upload failed for update:', errorText);
          throw new Error(`Cover upload failed: ${errorText}`);
        }
      }

      await fetchSets();
      setEditingSet(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteSet = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/sets/${setToDelete._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete set');
      }

      await fetchSets();
      setSetToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const openEditForm = (set) => {
    setEditingSet(set);
    setFormOpen(true);
  };

  const openDeleteDialog = (set) => {
    setSetToDelete(set);
    setDeleteDialogOpen(true);
  };

  const openPhotoManager = (set) => {
    setSelectedSetForPhotos(set);
    setPhotoManagerOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingSet(null);
  };

  const closePhotoManager = () => {
    setPhotoManagerOpen(false);
    setSelectedSetForPhotos(null);
  };

  const handlePhotosUpdated = (updatedSet) => {
    setSets((prev) => prev.map((set) => (set._id === updatedSet._id ? updatedSet : set)));
    setSelectedSetForPhotos((prev) =>
      prev && String(prev._id) === String(updatedSet._id) ? updatedSet : prev
    );
  };

  if (loading) {
    return <Typography>Loading sets...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>
          Add Set
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell width={56} />
              <TableCell>Name</TableCell>
              <TableCell width={88}>Price</TableCell>
              <TableCell width={140}>Character</TableCell>
              <TableCell>Instruction</TableCell>
              <TableCell width={88}>Photos</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 152 }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No sets
                </TableCell>
              </TableRow>
            ) : (
              sets.map((set) => {
                const thumb =
                  set.coverImage ||
                  (set.photos && set.photos.length > 0 ? set.photos[0].thumbnailUrl : null);
                const charName =
                  set.characterId && typeof set.characterId === 'object'
                    ? set.characterId.name
                    : '—';
                const tagPreview =
                  set.tags && set.tags.length > 0
                    ? set.tags.slice(0, 3).join(', ') + (set.tags.length > 3 ? '…' : '')
                    : '—';
                return (
                  <TableRow key={set._id} hover>
                    <TableCell>
                      <Avatar
                        variant="rounded"
                        src={thumb || undefined}
                        alt=""
                        sx={{ width: 44, height: 44, bgcolor: 'action.hover' }}
                      >
                        {!thumb ? <PhotoCamera sx={{ fontSize: 22, opacity: 0.5 }} /> : null}
                      </Avatar>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{set.name}</TableCell>
                    <TableCell>{set.price}</TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 140,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={charName}
                    >
                      {charName}
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 260,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                      title={set.instruction || ''}
                    >
                      {set.instruction ? (
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            maxWidth: '100%',
                            p: 0.75,
                            borderRadius: 1,
                            fontSize: '0.8125rem',
                            bgcolor: 'rgba(255,255,255,0.06)',
                            border: '1px solid',
                            borderColor: 'divider',
                            color: 'text.secondary',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'middle'
                          }}
                        >
                          {set.instruction}
                        </Box>
                      ) : (
                        '—'
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={set.photos?.length ?? 0}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.8125rem',
                        color: 'text.secondary'
                      }}
                      title={set.tags?.join(', ') || ''}
                    >
                      {tagPreview}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 152 }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          flexDirection: 'row',
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.25
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => openPhotoManager(set)}
                          title="Manage photos"
                        >
                          <PhotoLibrary />
                        </IconButton>
                        <IconButton size="small" onClick={() => openEditForm(set)} color="primary">
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => openDeleteDialog(set)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SetForm
        open={formOpen}
        onClose={closeForm}
        onSubmit={editingSet ? handleUpdateSet : handleCreateSet}
        set={editingSet}
        isEdit={!!editingSet}
        characters={characters}
      />

      {/* Photo Manager Dialog */}
      <Dialog
        open={photoManagerOpen}
        onClose={closePhotoManager}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: dialogFormPaperSx }}
      >
        <DialogTitle sx={dialogFormTitleSx}>
          Manage Photos - {selectedSetForPhotos?.name}
        </DialogTitle>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden'
          }}
        >
          <DialogContent sx={(theme) => dialogFormScrollContentSx(theme)}>
            {selectedSetForPhotos && (
              <SetPhotoManager
                setId={selectedSetForPhotos._id}
                photos={selectedSetForPhotos.photos || []}
                onPhotosUpdated={handlePhotosUpdated}
              />
            )}
          </DialogContent>
          <DialogActions sx={dialogFormActionsSx}>
            <Button onClick={closePhotoManager}>Close</Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Set</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{setToDelete?.name}"? This will also delete all associated photos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteSet} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SetList; 